import { Check, X } from 'lucide-react';
import { InlineMath } from 'react-katex';
import LatexRenderer from '../../Homework/LatexRenderer';
import SvgFigure from '../../Homework/SvgFigure';
import FigureTable, { figureTableOf } from '../../Homework/FigureTable';
import PipeTableBlocks, { hasPipeTable } from '../../Homework/PipeTable';

// Moved out of AIAssistant.jsx (2026-10-14) so Buffy's inline chips
// (BuffyInline) can draw the student-look question without loading the chat.

// A choice's "what picking this means" line: **bold** and inline LaTeX.
function ProseText({ text }) {
  const parts = String(text ?? '').split(/\*\*([^*]+)\*\*/g);
  return <span className="[&_.latex-renderer-container]:inline">{parts.map((p, i) => (!p ? null : i % 2 === 1
    ? <strong key={i}><LatexRenderer>{p}</LatexRenderer></strong> : <LatexRenderer key={i}>{p}</LatexRenderer>))}</span>;
}

// Prose from the tags and the bank often carries bare caret math ("6y^2") and
// the bank's $$\newline$$ markers — normalize so RichText/LatexRenderer show
// real math instead of raw carets (Sandy 2026-08-19: "bad rendering").
export const mathifyProse = (s) => String(s || '')
  // Never re-wrap text that is ALREADY inside $...$ — only bare segments.
  .split(/(\$[^$]*\$)/)
  .map((seg) => (seg.startsWith('$')
    ? seg
    : seg.replace(/([0-9a-zA-Z()+*/-]*\^[0-9a-zA-Z(){}]+[0-9a-zA-Z()+*/-]*)/g, (m) => `$${m}$`)))
  .join('');

// The bank's math items end questionText with the stem and repeat it in
// `question` ("Which statement is the best interpretation of the $y$-intercept
// …"). The student player draws `question` for non-math items only
// (QuestionCard), so the stem shows once there; here, a stem the text already
// holds is not drawn again (Fleet E 2026-10-26: the pop-up showed it twice,
// the second time with raw `$`). Compared without `$`, escapes and spacing.
const stemWords = (s) => String(s || '').replace(/\\([$%&#_])/g, '$1').replace(/\$/g, '')
  .replace(/\s+/g, ' ').trim().toLowerCase();
export function stemRepeats(text, question) {
  const stem = stemWords(question);
  return !!stem && stemWords(text).includes(stem);
}
// A stem with real `$…$` math (not an escaped \$40.00) draws as math, like the text.
const hasMath = (s) => /(^|[^\\])\$[^$]+\$/.test(String(s || ''));

// ── StudentQuestion (2026-08-19 v2, Sandy: "match our actual question
// rendering UI"): a read-only replica of the student player's QuestionCard —
// white choice rows with 2px state borders (#22c55e correct / #ef4444 their
// pick / #e5e7eb rest), the 40px SVG letter bubbles, green check / red X
// status icons. `preview` renders a quiet mini version for the breakout cards.
// an escaped "\$" (money) and no unescaped "$" (no math): plain text
const escapedOnly = (t) => typeof t === 'string' && /\\\$/.test(t) && !/(^|[^\\])\$/.test(t);

export function StudentQuestion({ q, preview = false }) {
  // Spacing in exact pixels (2026-10-14): Bootstrap's !important py-5 / mt-4 /
  // mt-5 / mt-3 / p-3 / gap-3 / mb-4 share Tailwind's names and won (48px
  // padding, 48px above the choices), overriding .buffy-question-paper too.
  // Choice bodies mirror the student player's branches (QuestionCard): pipe
  // tables, latexMC math ($-mixed via LatexRenderer, bare legacy via
  // InlineMath), SVG figure choices, then plain text.
  const choiceBody = (c) => {
    if (hasPipeTable(c.text)) return <PipeTableBlocks text={c.text} />;
    if (q.latexMC && c.text) {
      return /\$.+\$/.test(c.text)
        ? <LatexRenderer>{c.text}</LatexRenderer>
        : <InlineMath>{c.text}</InlineMath>;
    }
    if (c.figure?.svg) {
      return <SvgFigure svg={c.figure.svg} altText={c.figure.altText} size="compact" interactive={false} />;
    }
    // Money stored escaped ("\$31.40") with no math in it shows as plain text, as the student player shows it
    // (QuestionCard's raw path); LatexRenderer drew it in the math font (the 213-table review, 2026-11-02).
    if (escapedOnly(c.text)) return c.text.replace(/\\([$%&#_])/g, '$1');
    return <LatexRenderer>{c.text || ''}</LatexRenderer>;
  };
  return (
    <div className={preview ? '' : 'rounded-[16px] border border-neutral-200 bg-white px-6 py-[20px] shadow-[0_1px_2px_rgba(0,0,0,.04)]'}>
      {/* Student-player order (QuestionCard): figure, passage, stem, choices. */}
      {/* a figure with its table's data draws a real table, as the student player does (FigureTable) */}
      {!preview && figureTableOf(q.figure) && (
        <div className="mb-[16px]"><FigureTable table={figureTableOf(q.figure)} /></div>
      )}
      {!preview && !figureTableOf(q.figure) && q.figure?.svg && (
        <div className="mb-[16px]"><SvgFigure svg={q.figure.svg} altText={q.figure.altText} /></div>
      )}
      <div className={'text-gray-800 whitespace-pre-line ' + (preview ? 'text-[15px] leading-[1.6]' : 'text-[16px] leading-[1.75]')}>
        {hasPipeTable(q.questionText)
          ? <PipeTableBlocks text={q.questionText} center />
          : <LatexRenderer>{q.questionText}</LatexRenderer>}
      </div>
      {/* The stem ("Which choice…") is a separate field on English items —
          plain text with the player's LaTeX-escape unescape, not math. */}
      {q.question && !stemRepeats(q.questionText, q.question) && (
        <div className={'text-gray-800 whitespace-pre-line ' + (preview ? 'mt-2 text-[15px] leading-[1.6]' : 'mt-[16px] text-[16px] leading-[1.75]')}>
          {hasMath(q.question) ? <LatexRenderer>{q.question}</LatexRenderer> : String(q.question).replace(/\\([$%&#_])/g, '$1')}
        </div>
      )}
      <div className={'flex flex-col ' + (preview ? 'gap-2 mt-[12px]' : 'gap-2.5 mt-[20px]')}>
        {(q.choices || []).slice(0, preview ? 2 : q.choices?.length).map((c) => {
          const isCorrect = c.letter === q.correct;
          const isPicked = Boolean(q.student && c.letter === q.student.picked && !isCorrect);
          const ins = preview ? null : (q.choiceInsights || []).find((d) => d.letter === c.letter);
          return (
            <div
              key={c.letter}
              className="bg-white p-[12px] rounded-lg"
              style={{ border: '2px solid ' + (preview ? '#e5e7eb' : isCorrect ? '#22c55e' : isPicked ? '#ef4444' : '#e5e7eb') }}
            >
              <div className="flex items-center gap-[12px]">
                <svg width={preview ? 32 : 40} height={preview ? 32 : 40} viewBox="0 0 40 40" className="flex-none">
                  <circle
                    cx="20" cy="20" r="16"
                    fill={!preview && isPicked ? '#ef4444' : '#ffffff'}
                    stroke={!preview && isPicked ? '#b91c1c' : '#000000'}
                    strokeWidth="2"
                  />
                  <text
                    x="20" y="20" textAnchor="middle" dominantBaseline="central"
                    fontSize="20" fontWeight="500"
                    fill={!preview && isPicked ? '#ffffff' : '#374151'}
                  >
                    {c.letter}
                  </text>
                </svg>
                <span className={'text-gray-800 min-w-0 flex-1 ' + (preview ? 'text-[14px]' : 'text-[15px]')}>
                  {choiceBody(c)}
                </span>
                {!preview && isCorrect && <Check className="flex-none text-green-500" size={22} strokeWidth={2.5} />}
                {!preview && isPicked && <X className="flex-none text-red-500" size={22} strokeWidth={2.5} />}
              </div>
              {ins?.means && (
                <div className="mt-1.5 pl-[52px] text-[12.5px] text-neutral-500 leading-snug">
                  {ins.pct != null && <span className="font-semibold text-neutral-700">{Math.round(ins.pct)}% pick this: </span>}
                  <ProseText text={mathifyProse(ins.means)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

